import * as THREE from 'three';
import { CONFIG } from '../config';
import { clamp, damp, dampAngle } from '../core/math';
import type { CharacterLook } from '../state/types';
import { buildCharacter, CharacterAnimator, type CharacterRig, type Pose } from '../world/builders/character';
import type { CollisionWorld } from '../world/Collision';

export interface MoveIntent {
  /** Desired world-space direction (length 0..1). */
  dx: number;
  dz: number;
  sprint: boolean;
  jump: boolean;
}

export interface BusyAction {
  pose: Pose;
  until: number;
  seat?: number;
  facing?: number;
  onDone?: () => void;
}

/** The player character: movement, collision and animation state. */
export class Player {
  rig: CharacterRig;
  anim: CharacterAnimator;
  readonly position = new THREE.Vector3();
  facing = 0;
  speed = 0;
  sprinting = false;
  y = 0;
  private vy = 0;
  private readonly vel = new THREE.Vector2();
  busy: BusyAction | null = null;
  carrying: THREE.Object3D | null = null;
  pushing: THREE.Object3D | null = null;
  hidden = false;
  /** Hidden from your own camera only (first person); other players still see you. */
  viewHidden = false;
  /** External code (stunts) drives the rig while true. */
  scripted = false;
  /**
   * Sitting on something that moves you (the Lil' Rascal). Whatever owns it moves you
   * around; the player just sits, and a beer or a bite happens in the seat.
   */
  mount: { seat: number } | null = null;
  /** Where you can walk from a given spot: the park, or whichever room you're standing in. */
  boundsAt: (x: number, z: number) => { minX: number; maxX: number; minZ: number; maxZ: number } = () =>
    CONFIG.bounds;

  constructor(
    private readonly parent: THREE.Object3D,
    look: CharacterLook,
  ) {
    this.rig = buildCharacter(look);
    this.anim = new CharacterAnimator(this.rig);
    parent.add(this.rig.root);
  }

  setLook(look: CharacterLook): void {
    this.parent.remove(this.rig.root);
    const carry = this.carrying;
    this.rig = buildCharacter(look);
    this.anim = new CharacterAnimator(this.rig);
    this.parent.add(this.rig.root);
    if (carry) this.rig.carry.add(carry);
    this.syncTransform();
  }

  teleport(x: number, z: number, facing = this.facing): void {
    this.position.set(x, 0, z);
    this.facing = facing;
    this.vel.set(0, 0);
    this.y = 0;
    this.vy = 0;
    this.syncTransform();
  }

  doBusy(action: BusyAction): void {
    this.busy = action;
    this.vel.set(0, 0);
    if (action.facing !== undefined) this.facing = action.facing;
  }

  setCarrying(obj: THREE.Object3D | null): void {
    if (this.carrying) this.carrying.removeFromParent();
    this.carrying = obj;
    if (obj) {
      obj.position.set(0, 0, 0);
      obj.rotation.set(0, 0, 0);
      this.rig.carry.add(obj);
    }
  }

  update(
    dt: number,
    time: number,
    intent: MoveIntent,
    collision: CollisionWorld,
    opts: { buzz: number; energy: number; poseOverride?: Pose },
  ): void {
    this.rig.root.visible = !this.hidden && !this.viewHidden;
    if (this.scripted) return;

    if (this.busy) {
      const p = this.busy.pose;
      const seated = this.mount && (p === 'drink' || p === 'eat');
      this.anim.update({
        dt,
        time,
        speed: 0,
        pose: seated ? 'scootdrink' : p,
        seat: seated ? this.mount?.seat : this.busy.seat,
      });
      if (time >= this.busy.until) {
        const done = this.busy.onDone;
        this.busy = null;
        done?.();
      }
      this.syncTransform();
      return;
    }

    let dx = intent.dx;
    let dz = intent.dz;
    // Drunk walking: the world gently tilts under you.
    const wobble = clamp((opts.buzz - 55) / 45, 0, 1);
    if (wobble > 0 && (dx !== 0 || dz !== 0)) {
      const a = Math.sin(time * 1.9) * 0.55 * wobble + Math.sin(time * 4.3) * 0.15 * wobble;
      const c = Math.cos(a);
      const s = Math.sin(a);
      const ndx = dx * c - dz * s;
      dz = dx * s + dz * c;
      dx = ndx;
    }
    const burdened = this.carrying !== null || this.pushing !== null;
    this.sprinting = intent.sprint && !burdened && opts.energy > 5 && (dx !== 0 || dz !== 0);
    let max = this.sprinting ? CONFIG.player.sprintSpeed : CONFIG.player.walkSpeed;
    if (this.carrying) max *= CONFIG.player.carrySpeedFactor;
    else if (this.pushing) max *= CONFIG.player.pushSpeedFactor;
    if (opts.energy < 15) max *= 0.75;
    this.vel.x = damp(this.vel.x, dx * max, 12, dt);
    this.vel.y = damp(this.vel.y, dz * max, 12, dt);

    const next = { x: this.position.x + this.vel.x * dt, z: this.position.z + this.vel.y * dt };
    collision.resolve(next, CONFIG.player.radius);
    const b = this.boundsAt(this.position.x, this.position.z);
    next.x = clamp(next.x, b.minX, b.maxX);
    next.z = clamp(next.z, b.minZ, b.maxZ);
    const moved = Math.hypot(next.x - this.position.x, next.z - this.position.z);
    this.speed = dt > 0 ? moved / dt : 0;
    this.position.x = next.x;
    this.position.z = next.z;

    if (Math.hypot(this.vel.x, this.vel.y) > 0.3)
      this.facing = dampAngle(this.facing, Math.atan2(this.vel.x, this.vel.y), 12, dt);

    if (intent.jump && this.y === 0 && !burdened) this.vy = CONFIG.player.jumpVelocity;
    if (this.vy !== 0 || this.y > 0) {
      this.vy -= CONFIG.player.gravity * dt;
      this.y += this.vy * dt;
      if (this.y <= 0) {
        this.y = 0;
        this.vy = 0;
      }
    }

    const pose: Pose = opts.poseOverride ?? (this.carrying ? 'carry' : this.pushing ? 'push' : 'stand');
    this.anim.update({
      dt,
      time,
      speed: this.speed,
      pose,
      sprinting: this.sprinting,
      airborne: this.y > 0.05,
      seat: this.mount?.seat,
    });
    this.syncTransform();
  }

  syncTransform(): void {
    this.rig.root.position.set(this.position.x, this.y, this.position.z);
    this.rig.root.rotation.y = this.facing;
  }

  /** Where the eyes are, for the first-person view. */
  eye(out = new THREE.Vector3()): THREE.Vector3 {
    return this.rig.head.localToWorld(out.set(0, 0.19, 0.2));
  }

  /** World position of the head (for labels). */
  headPosition(out = new THREE.Vector3()): THREE.Vector3 {
    return out.set(this.position.x, this.y + 2.1 * this.rig.look.height, this.position.z);
  }
}
